/**
 * SBTerms — SB-Vertrag / AGB zwischen ivoliq und Steuerberatern (v1.0, Juni 2026).
 * Route: /steuerberater-agb (öffentlich, verlinkt aus Tarifwechsel-, Lizenz- und Onboarding-Dialogen).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Scale, Package, RefreshCw, Users, CreditCard, AlertTriangle,
  CalendarX, ShieldCheck, Lock, Gavel, FileText,
} from 'lucide-react';
import { useSEO } from '@/hooks/useSEO';

const SectionCard = ({ icon: Icon, title, children, testId }) => (
  <Card data-testid={testId}>
    <CardHeader>
      <CardTitle className="flex items-center gap-2 text-base">
        <Icon className="h-5 w-5 text-emerald-600 shrink-0" />
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-3 text-sm text-gray-700">{children}</CardContent>
  </Card>
);

export default function SBTerms() {
  useSEO({
    title: 'SB-Vertrag (AGB für Steuerberater) | ivoliq',
    description: 'Vertragsbedingungen für die Nutzung des ivoliq SB-Portals durch Steuerberater und Kanzleien — Tarife, Abrechnung, Kündigung, Mandantenlizenzen.',
    canonical: 'https://ivoliq.io/steuerberater-agb',
    robots: 'index,follow',
  });

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="sb-terms-page">
      <div className="mb-8">
        <Link to="/" className="text-emerald-600 hover:text-emerald-700 mb-4 inline-block">
          ← Zurück
        </Link>
        <h1 className="text-4xl font-bold text-gray-900 mb-2">
          ivoliq SB-Vertrag
        </h1>
        <p className="text-gray-600">
          Allgemeine Geschäftsbedingungen für Steuerberater und Kanzleien („SB-Vertrag")
        </p>
        <p className="text-sm text-gray-500 mt-2">Version 1.0 · Stand: Juni 2026</p>
      </div>

      <div className="space-y-6">
        <SectionCard icon={Scale} title="§ 1 Geltungsbereich & Vertragsparteien" testId="sb-terms-scope">
          <p>
            Dieser Vertrag regelt die Nutzung des ivoliq SB-Portals (sb.ivoliq.io) durch
            Steuerberater, Steuerberatungsgesellschaften und Buchhaltungskanzleien
            (nachfolgend „Kanzlei") und die Geschäftsbeziehung mit der ivoliq GmbH mit Sitz in
            Österreich (nachfolgend „ivoliq").
          </p>
          <div className="bg-orange-50 border-l-4 border-orange-500 p-4">
            <p className="font-semibold text-gray-800">
              ⚠️ Dieser Vertrag richtet sich ausschließlich an Unternehmer im Sinne des
              § 1 UGB (Österreich) bzw. § 14 BGB (Deutschland). Verbraucher sind von der
              Nutzung des SB-Portals ausgeschlossen.
            </p>
          </div>
          <p>
            Mit der Auswahl eines Tarifs, der Übernahme einer Mandantenlizenz oder dem
            Anlegen eines Mandanten-Accounts im SB-Portal stimmt die Kanzlei diesem Vertrag
            ausdrücklich zu. Die Zustimmung wird mit Zeitstempel und Vertragsversion protokolliert.
          </p>
        </SectionCard>

        <SectionCard icon={Package} title="§ 2 Leistungen & Tarife" testId="sb-terms-tiers">
          <p>ivoliq stellt der Kanzlei das SB-Portal in folgenden Tarifen zur Verfügung:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Starter:</strong> 0 € / Monat — bis zu 5 aktive Mandanten</li>
            <li><strong>Growth:</strong> 39 € / Monat — bis zu 25 aktive Mandanten</li>
            <li><strong>Pro:</strong> 99 € / Monat — unbegrenzte Mandantenzahl</li>
          </ul>
          <p>
            Zusätzlich kann die Kanzlei <strong>Mandantenlizenzen</strong> zu je
            <strong> 16 € / Mandant / Monat</strong> übernehmen (siehe § 4). Alle Preise
            verstehen sich netto, zuzüglich gesetzlicher Umsatzsteuer, soweit anwendbar.
          </p>
        </SectionCard>

        <SectionCard icon={RefreshCw} title="§ 3 Tarifwechsel (Upgrade / Downgrade)" testId="sb-terms-tierchange">
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <strong>Upgrade:</strong> jederzeit möglich und sofort wirksam. Der laufende
              Kalendermonat wird vollständig zum neuen (höheren) Tarif abgerechnet.
            </li>
            <li>
              <strong>Downgrade:</strong> zum Ende des laufenden Kalendermonats möglich.
              Der neue (niedrigere) Tarif gilt ab dem 1. des Folgemonats. Ein Downgrade ist
              nur möglich, wenn die Anzahl der aktiven Mandanten das Limit des Zieltarifs
              nicht überschreitet.
            </li>
            <li>
              Der Tarifwechsel erfolgt eigenständig im SB-Portal und wird mit Zustimmung zu
              diesem Vertrag sofort aktiviert — eine gesonderte Freischaltung durch ivoliq
              ist nicht erforderlich.
            </li>
          </ul>
        </SectionCard>

        <SectionCard icon={Users} title="§ 4 Mandantenlizenzen & Kostenübernahme" testId="sb-terms-licenses">
          <p>
            Die Kanzlei kann für ihre Mandanten die ivoliq Pro-Lizenz übernehmen
            („Kanzlei-Lizenz", 16 € / Mandant / Monat). Die Übernahme erfordert eine
            ausdrückliche Bestätigung der Kostenübernahme im SB-Portal.
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <strong>Volle Monatsverrechnung:</strong> Jeder angefangene Kalendermonat wird
              vollständig berechnet — auch wenn ein Mandant erst im Laufe des Monats
              hinzukommt.
            </li>
            <li>
              Die Lizenzpflicht endet mit der Beendigung des Mandats im SB-Portal. Bereits
              angefangene Monate werden voll abgerechnet.
            </li>
            <li>
              Beim Anlegen eines Mandanten-Accounts durch die Kanzlei wird der Mandant in
              der Aktivierungs-E-Mail ausdrücklich über die von der Kanzlei erteilten
              Vollmachten informiert und kann diese jederzeit einschränken oder widerrufen.
            </li>
          </ul>
        </SectionCard>

        <SectionCard icon={CreditCard} title="§ 5 Abrechnung & Zahlung" testId="sb-terms-payment">
          <ul className="list-disc pl-5 space-y-2">
            <li>
              Die Abrechnung erfolgt <strong>am Ende der vereinbarten Abrechnungsperiode</strong>
              (Standard: monatlich; auf Wunsch quartalsweise, halbjährlich oder jährlich).
            </li>
            <li>
              Rechnungen werden per E-Mail (billing@ivoliq.io) als PDF zugestellt und sind per
              Überweisung zahlbar.
            </li>
            <li>
              <strong>Zahlungsziel: 10 Tage</strong> nach Rechnungserhalt, ohne Abzug.
            </li>
            <li>
              Umsatzsteuerliche Behandlung: Kanzleien mit Sitz in Österreich 20 % USt;
              EU-Ausland Reverse Charge (Art. 196 MwStSystRL); Drittland nicht im Inland
              steuerbar (§ 3a UStG).
            </li>
          </ul>
        </SectionCard>

        <SectionCard icon={AlertTriangle} title="§ 6 Zahlungsverzug & Mahnwesen" testId="sb-terms-dunning">
          <p>Bei Nichtzahlung innerhalb des Zahlungsziels gilt folgender Ablauf (jeweils ab Rechnungsdatum):</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Nach 14 Tagen:</strong> Zahlungserinnerung (kostenfrei)</li>
            <li><strong>Nach 28 Tagen:</strong> 1. Mahnung — Mahngebühr 5 €</li>
            <li><strong>Nach 42 Tagen:</strong> 2. Mahnung — Mahngebühr 10 €</li>
          </ul>
          <p>
            Bleibt die Zahlung auch nach der 2. Mahnung aus, ist ivoliq berechtigt, den
            Zugang zum SB-Portal zu sperren und weitere rechtliche Schritte (Inkasso)
            einzuleiten. Gesetzliche Verzugszinsen bleiben unberührt.
          </p>
        </SectionCard>

        <SectionCard icon={CalendarX} title="§ 7 Laufzeit & Kündigung" testId="sb-terms-cancellation">
          <ul className="list-disc pl-5 space-y-2">
            <li>Der Vertrag läuft auf unbestimmte Zeit.</li>
            <li>
              <strong>Kündigungsfrist:</strong> Die Kanzlei kann den Vertrag jederzeit
              <strong> monatlich zum Ende des Kalendermonats</strong> kündigen — in Textform
              (E-Mail an admin@ivoliq.io) oder über das SB-Portal.
            </li>
            <li>
              Bis zum Wirksamwerden der Kündigung erbrachte Leistungen (Grundgebühr,
              Mandantenlizenzen) werden regulär abgerechnet.
            </li>
            <li>
              Übernommene Mandantenlizenzen enden mit dem Vertrag; betroffene Mandanten
              erhalten eine Übergangsfrist von 30 Tagen, um ihre Lizenz selbst fortzuführen.
            </li>
            <li>
              Das Recht zur außerordentlichen Kündigung aus wichtigem Grund bleibt für beide
              Parteien unberührt.
            </li>
          </ul>
        </SectionCard>

        <SectionCard icon={ShieldCheck} title="§ 8 Pflichten der Kanzlei" testId="sb-terms-duties">
          <ul className="list-disc pl-5 space-y-2">
            <li>
              Die Kanzlei stellt sicher, dass die beim Anlegen von Mandanten-Accounts
              erfassten Daten korrekt sind und eine entsprechende Beauftragung durch den
              Mandanten vorliegt.
            </li>
            <li>
              Die Kanzlei hält ihre Rechnungsdaten (Anschrift, UID-Nummer) im SB-Portal
              aktuell.
            </li>
            <li>
              Zugangsdaten sind vertraulich zu behandeln und vor dem Zugriff Dritter zu
              schützen.
            </li>
          </ul>
        </SectionCard>

        <SectionCard icon={Lock} title="§ 9 Datenschutz" testId="sb-terms-privacy">
          <p>
            Für die Verarbeitung personenbezogener Mandantendaten über das SB-Portal gilt der{' '}
            <Link to="/steuerberater-avv" className="text-emerald-600 hover:underline">SB-Auftragsverarbeitungsvertrag (SB-AVV)</Link>,
            der gemeinsam mit diesem Vertrag geschlossen wird. Ergänzend gilt die{' '}
            <Link to="/privacy" className="text-emerald-600 hover:underline">Datenschutzerklärung</Link> von ivoliq.
            Soweit Mandanten eigene ivoliq-Nutzer sind, gilt für deren Verhältnis zu ivoliq
            zusätzlich der <Link to="/avv" className="text-emerald-600 hover:underline">Nutzer-AVV</Link>.
          </p>
        </SectionCard>

        <SectionCard icon={Gavel} title="§ 10 Haftung" testId="sb-terms-liability">
          <p>
            ivoliq haftet unbeschränkt für Vorsatz und grobe Fahrlässigkeit sowie für Schäden
            aus der Verletzung von Leben, Körper oder Gesundheit. Bei leichter Fahrlässigkeit
            haftet ivoliq nur für die Verletzung wesentlicher Vertragspflichten
            (Kardinalpflichten), begrenzt auf den vertragstypischen, vorhersehbaren Schaden.
            ivoliq ersetzt keine steuerliche oder rechtliche Beratung; die fachliche
            Verantwortung für die Mandatsarbeit verbleibt bei der Kanzlei.
          </p>
        </SectionCard>

        <SectionCard icon={FileText} title="§ 11 Schlussbestimmungen" testId="sb-terms-final">
          <ul className="list-disc pl-5 space-y-2">
            <li>Es gilt österreichisches Recht unter Ausschluss des UN-Kaufrechts.</li>
            <li>Gerichtsstand ist der Sitz der ivoliq GmbH (Wien, Österreich).</li>
            <li>
              Sollten einzelne Bestimmungen dieses Vertrags unwirksam sein, bleibt die
              Wirksamkeit der übrigen Bestimmungen unberührt (salvatorische Klausel).
            </li>
            <li>
              <strong>Vertragsänderungen:</strong> Nicht nachteilige Änderungen dieses Vertrags
              (z.B. neue Funktionen, Klarstellungen, Anpassungen an geänderte Rechtslage) werden
              mit Veröffentlichung wirksam. Wesentliche nachteilige Änderungen (insbesondere
              Preiserhöhungen oder Leistungskürzungen) kündigt ivoliq mindestens 14 Tage vor
              Inkrafttreten in Textform oder durch deutlichen Hinweis im SB-Portal an.
              Widerspricht die Kanzlei nicht bis zum Inkrafttreten oder nutzt sie das Portal
              danach weiter, gelten die Änderungen als angenommen; bis zum Inkrafttreten kann
              sie ordentlich kündigen (§ 7).
            </li>
          </ul>
        </SectionCard>
      </div>

      <p className="mt-8 text-xs text-gray-400">
        ivoliq GmbH · Österreich · Fragen zum Vertrag: admin@ivoliq.io
      </p>
    </div>
  );
}
